/**
 * Ricevuta permanente della consegna e registrazione dell'esito esterno.
 *
 * Offline e' sola lettura: accepted/rejected sono atti verso il committente,
 * e una scrittura in coda locale sarebbe un esito che il servente non ha mai
 * visto. Lo snapshot cacheato porta l'ora di cattura, non si finge aggiornato.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ServiceError } from '../api'
import { EXPORT_OUTCOME, type Whoami } from '../auth'
import { StatusPill } from '../carta/StatusPill'
import * as exports from '../exports'
import type { Receipt } from '../exports'
import { moment, notMeasured } from '../format'
import { readSnapshot, writeSnapshot } from '../readSnapshots'
import { useT } from '../i18n/LocaleProvider'

export const RICEVUTA_EXPORT = 'ricevuta-export'

export function ExportReceipt({ who = null }: { who?: Whoami | null }): JSX.Element {
  const t = useT()
  const exportId = useParams().exportId ?? ''
  const [receipt, setReceipt] = useState<Receipt | null>(null)
  const [snapshotAt, setSnapshotAt] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [reason, setReason] = useState('')
  const [reference, setReference] = useState('')
  const [online, setOnline] = useState(window.navigator.onLine)
  const [file, setFile] = useState<string | null>(null)
  const heading = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    const onState = (): void => setOnline(window.navigator.onLine)
    window.addEventListener('online', onState)
    window.addEventListener('offline', onState)
    return () => {
      window.removeEventListener('online', onState)
      window.removeEventListener('offline', onState)
    }
  }, [])

  useEffect(() => {
    heading.current?.focus()
  }, [receipt?.state])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const found = await exports.receipt(exportId)
        if (cancelled) return
        setReceipt(found)
        setSnapshotAt(null)
        void writeSnapshot('export-receipt', exportId, found).catch(() => undefined)
      } catch (error: unknown) {
        const snapshot = await readSnapshot<Receipt>('export-receipt', exportId).catch(() => null)
        if (snapshot !== null) {
          if (!cancelled) {
            setReceipt(snapshot.value)
            setSnapshotAt(snapshot.captured_at)
          }
          return
        }
        if (!cancelled) {
          setFailure(error instanceof ServiceError ? error.message : t('receipt.unread'))
        }
      }
    })().catch((error: unknown) => {
      if (cancelled) return
      setFailure(error instanceof ServiceError ? error.message : t('receipt.unread'))
    })
    return () => {
      cancelled = true
    }
  }, [exportId])

  function mayOutcome(): boolean {
    return who == null || who.may.includes(EXPORT_OUTCOME)
  }

  function run(work: () => Promise<unknown>): void {
    if (!online) return
    setBusy(true)
    setFailure(null)
    work()
      .then(async () => {
        const found = await exports.receipt(exportId)
        setReceipt(found)
        setSnapshotAt(null)
      })
      .catch((error: unknown) => {
        setFailure(error instanceof ServiceError ? error.message : t('export.outcomeFailed'))
      })
      .finally(() => setBusy(false))
  }

  if (failure !== null && receipt === null) {
    return (
      <main>
        <h2 ref={heading} tabIndex={-1}>
          {t('receipt.unavailable')}
        </h2>
        <p>{failure}</p>
      </main>
    )
  }
  if (receipt === null) {
    return (
      <main>
        <p role="status">{t('receipt.reading')}</p>
      </main>
    )
  }

  const submitted = receipt.state === 'submitted'
  const offline = !online

  return (
    <main className="consegna">
      {/* Il percorso (GI-279): la ricevuta si apre sei mesi dopo, e chi la
          apre deve poter tornare all'elenco delle consegne o alla commessa
          senza rifare la strada dal pool. */}
      <nav className="workspace-percorso percorso-pagina" aria-label={t('crumb.path')}>
        <Link to="/exports" className="workspace-percorso-radice">
          {t('receipt.openDeliveries')}
        </Link>
        {receipt.inspection_id != null && receipt.external_inspection_id != null && (
          <>
            <span className="workspace-percorso-sep" aria-hidden="true">
              ›
            </span>
            <Link to={`/inspections/${receipt.inspection_id}/delivery`} className="workspace-percorso-radice t-num">
              {receipt.external_inspection_id}
            </Link>
          </>
        )}
        <span className="workspace-percorso-sep" aria-hidden="true">
          ›
        </span>
        <span className="workspace-percorso-schermata" aria-current="page">
          {t('delivery.step.receipt')}
        </span>
      </nav>
      <h2 ref={heading} tabIndex={-1}>
        {t('delivery.step.receipt')}
      </h2>
      {offline && <p role="status">{t('export.offline')}</p>}
      {snapshotAt && (
        <p className="tenue" role="status">
          {t('export.snapshot', { when: moment(snapshotAt) })}
        </p>
      )}
      {failure && <p className="avviso">{failure}</p>}
      <section className="ricevuta">
        <div className="riga-ricevuta">
          <StatusPill
            label={receipt.state === 'submitted' ? t('pool.statusDelivered') : receipt.state}
            tone={receipt.state === 'submitted' ? 'st-lock' : 'ink-3'}
          />
          <span className="t-num">{moment(receipt.issued_at)}</span>
        </div>
        <dl className="t-num">
          <dt>{t('delivery.declarations')}</dt>
          <dd>{receipt.issues}</dd>
          <dt>{t('delivery.operator')}</dt>
          <dd>{receipt.author ?? notMeasured()}</dd>
          <dt>{t('delivery.catalog')}</dt>
          <dd>{receipt.catalog_version}</dd>
          <dt>{t('delivery.sha256')}</dt>
          <dd>{receipt.checksum}</dd>
        </dl>
        {receipt.warning_note && (
          <p>
            <span className="tenue">{t('delivery.sentWithWarnings')}</span>
            {receipt.warning_note}
          </p>
        )}
        {receipt.rejection_reason && (
          <p className="guasto">
            {t('delivery.rejected', { reason: receipt.rejection_reason })}
            {receipt.external_reference && ` · ${receipt.external_reference}`}
          </p>
        )}
        <p>
          {file === null ? (
            <button
              type="button"
              onClick={() =>
                void exports
                  .document(exportId)
                  .then((body) => {
                    setFile(
                      URL.createObjectURL(
                        new Blob([JSON.stringify(body, null, 1)], { type: 'application/json' }),
                      ),
                    )
                  })
                  .catch((error: unknown) => {
                    setFailure(
                      error instanceof ServiceError
                        ? error.message
                        : t('export.docFailed'),
                    )
                  })
              }
            >
              {t('delivery.prepareDoc')}
            </button>
          ) : (
            <a href={file} download={`consegna-${receipt.checksum.slice(0, 8)}.json`}>
              {t('delivery.downloadDoc')}
            </a>
          )}
        </p>
      </section>

      {submitted && (
        <section>
          <h3>{t('export.customerOutcome')}</h3>
          <p>
            <button
              type="button"
              className="primario"
              disabled={offline || busy || !mayOutcome()}
              onClick={() => run(() => exports.accepted(exportId))}
            >
              {t('export.accept')}
            </button>
          </p>
          <p>
            <label>
              {t('export.rejectReason')}
              <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} />
            </label>
          </p>
          <p>
            <label>
              {t('export.externalRef')}
              <input
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            </label>
          </p>
          <p>
            <button
              type="button"
              disabled={offline || busy || !reason.trim() || !reference.trim() || !mayOutcome()}
              onClick={() => run(() => exports.rejected(exportId, reason.trim(), reference.trim()))}
            >
              {t('export.reject')}
            </button>
          </p>
        </section>
      )}
    </main>
  )
}
